import { defineComponent, h, withDirectives, resolveDirective } from 'vue';
import { QSelect, QItem, QItemSection, QItemLabel } from 'quasar';
import BaseComponent from './baseFormMixin.jsx';
import variableBindingMixin from './variableBindingMixin.jsx';
import utils from '../../../Shared/utils.jsx';
import methods from '../../../Shared/methods';
import careHelpfulFunctions from '../../careHelpfulFunctions.jsx';
import { appSettings } from '@/Shared/appSettings.js';

import TranslationContainer from '../TranslationContainer.jsx';

const NOT_FOUND_TEXT = '-Not Found-';

export default defineComponent({
    name: 'sform-lookuplist',
    mixins: [BaseComponent, variableBindingMixin],
    components: {
        QSelect,
        QItem,
        QItemSection,
        QItemLabel,
        TranslationContainer
    },
    data: () => ({
        dolookupexpn: null,
        lookupurlexpn: null,
        lookuprawexpn: null,

        displayexpnfunc: null,
        valueexpnfunc: null,

        item_text: null,
        item_value: null,
        item_disabled: undefined,
        lookupitems: [],
        loading: false,

        arrayfield: null,
        arrayelement: null,
        keyextractor: null,

        filter: '',
    }),
    props: {
    },
    created() {
        if (this.element.formatData) {
            if (this.element.formatData.LookupExpression)
                this.dolookupexpn = utils.compileExpression(this, this.element.formatData.LookupExpression);

            if ((!this.element.formatData.LookupType || this.element.formatData.LookupType == 'URL') && this.element.formatData.LookupURL)
                this.lookupurlexpn = utils.compile(this, this.element.formatData.LookupURL);
            else if (this.element.formatData.LookupType == 'Interpolated' && this.element.formatData.LookupInterpolatedExpression)
                this.lookuprawexpn = utils.compileExpression(this, this.element.formatData.LookupInterpolatedExpression);

            if (this.element.formatData.DisplayType == 'Expression') {
                const expr = utils.parseExpressionWithFilters(this.element.formatData.DisplayExpression);
                this.displayexpnfunc = {
                    code: expr,
                    func: new Function('control', 'util', this.element.formatData.LookupModelAs, `with (control) return ${expr};`)
                };
            }
            if (this.element.formatData.ValueType == 'Expression') {
                const expr = utils.parseExpressionWithFilters(this.element.formatData.ValueExpression);
                this.valueexpnfunc = {
                    code: expr,
                    func: new Function('control', 'util', this.element.formatData.LookupModelAs, `with (control) return ${expr};`)
                };
            }

            switch (this.element.formatData.DisplayType) {
                case 'Fieldname':
                    // The '-Not Found-' and blank placeholder rows cannot carry their own label when
                    // DisplayField and ValueField name the same property. Resolve labels here instead.
                    this.item_text = (data) => {
                        if (data && data.$notfound) return NOT_FOUND_TEXT;
                        if (data && data.$emptyrow) return this.emptyItemLabel();
                        return this.getByPath(data, this.element.formatData.DisplayField);
                    };
                    break;
                case 'Expression':
                    this.item_text = (data) => {
                        try {
                            if (data && data.$notfound) return NOT_FOUND_TEXT;
                            if (data && data.$emptyrow) return this.emptyItemLabel();
                            return this.displayexpnfunc.func(this, careHelpfulFunctions, data);
                        }
                        catch (e) {
                            utils.warn(`LookupList DisplayType Expression failed to evaluate: ${this.displayexpnfunc.code}; original: ${this.element.formatData.DisplayExpression}`, e);
                            return '';
                        }
                    };
                    break;
            }
            switch (this.element.formatData.ValueType) {
                case 'Fieldname': this.item_value = this.element.formatData.ValueField; break;
                case 'Expression':
                    this.item_value = (data) => {
                        try {
                            return data.$emptyrow ? null : this.valueexpnfunc.func(this, careHelpfulFunctions, data);
                        }
                        catch (e) {
                            utils.warn(`LookupList ValueType Expression failed to evaluate: ${this.valueexpnfunc.code}; original: ${this.element.formatData.ValueExpression}`, e);
                            return '';
                        }
                    };
                    break;
            }

            if (this.element.formatData.UniqueValues) {
                // If this item is marked for uniqueness, then we must locate the array where
                // the list of items are held. Then as we populate the items for the dropdown,
                // we can filter out items that have already been selected in another list.
                this.arrayfield = this.ParentByType('FormField_ArrayItem')?.Parent;
                if (this.arrayfield) {
                    // Find the model reference that will allow us to read the array element from all
                    // other array items.
                    const keys = [...this.element.key];  // Ex: Queues.[].QueueName
                    const arraykeys = this.ParentByType('FormField_ArrayItem').element.key; // Ex: Queues.[]
                    keys.splice(0, arraykeys.length); // Ex: QueueName - isolate the element from the array down

                    // This is sufficient for 99% of the use cases, but if the key happens to be in an array
                    // for example Queues.[].Another.[1].QueueName, then this will fail. But I don't expect this
                    // to ever be a real issue, so the effort to support it isn't worth it. (7/11/2021)
                    const item = keys.join('.');
                    if (item)
                        this.keyextractor = new Function('item', `return item.${item};`);
                    else
                        this.keyextractor = new Function('item', `return item;`);

                    this.arrayelement = this.arrayfield.FieldValue;
                    if (this.arrayelement) {
                        if (!this.arrayfield.chosenitems) this.arrayfield.chosenitems = {};
                        if (!this.arrayfield.availableitems) this.arrayfield.availableitems = {};
                        this.arrayfield.chosenitems[this.chosenitems_key] = Object.fromEntries(this.arrayelement.map(a => [this.keyextractor(a), true]));
                        this.item_disabled = (item) => this.getItemValue(item) != this.itemvalue && this.getItemValue(item) in this.arrayfield.chosenitems[this.chosenitems_key];
                    }
                }
            }

            // Honor per-option disabled (e.g. API marks N/A for non-admins) and UniqueValues disabling.
            const uniqueDisabled = typeof this.item_disabled === 'function' ? this.item_disabled : null;
            this.item_disabled = (item) => !!(item && item.disabled) || (uniqueDisabled ? uniqueDisabled(item) : false);
        }

        if (this.lookupurlexpn)
            this.lookupurl_watch$ = this.$watch(
                function () {
                    return this.lookupurl;
                },
                function (newval, oldval) {
                    this.Refresh();
                }
            );

        if (this.lookuprawexpn)
            this.lookupraw_watch$ = this.$watch(
                function () {
                    return this.lookupraw;
                },
                function (newval, oldval) {
                    this.Refresh();
                }
            );
    },
    unmounted() {
        if (this.lookupurl_watch$)
            this.lookupurl_watch$();

        if (this.lookupraw_watch$)
            this.lookupraw_watch$();

        if (this.arrayfield && this.arrayelement) {
            // Uniqueness has been requested and we have a reference to the array of items already picked.
            // We need to generate a lookup object containing every selected item so that we can disable
            // those that are already picked in the current dropdown list.
            if (!this.arrayfield.chosenitems) this.arrayfield.chosenitems = {};
            this.arrayfield.chosenitems[this.chosenitems_key] = Object.fromEntries(this.arrayelement.map(a => [this.keyextractor(a), true]));

            // Update the array container to control if the Add button is enabled or not
            this.arrayfield.availableitems[this.chosenitems_key] = this.lookupitems && (this.arrayelement.length < this.lookupitems.length);
        }
    },
    async mounted() {
        await this.Refresh();
        this.loadComplete();
    },
    computed: {
        chosenitems_key: function () {
            // Generate a key that is made from the name. Ex: Queue.[0].Name ==> Queue__0__Name
            return this.name.replace(/\./g, '_').replace(/\[/g, '_').replace(/\]/g, '_');
        },
        tolookup: function () {
            if (this.dolookupexpn)
                return utils.evaluate(this.dolookupexpn, this);
            else
                return true;
        },
        lookupurl: function () {
            if (this.lookupurlexpn)
                try {
                    return utils.evaluate(this.lookupurlexpn, this, false, null, true);
                }
                catch (e) {
                    utils.warn(`LookupList lookupurl could not be evaluated: ${this.lookupurlexpn.code}`, e);
                    return '';
                }
            else
                return '';
        },
        lookupraw: function () {
            if (this.lookuprawexpn)
                try {
                    return utils.evaluate(this.lookuprawexpn, this, false, null, true);
                }
                catch (e) {
                    utils.warn(`LookupList lookupraw could not be evaluated: ${this.lookuprawexpn.code}`, e);
                    return [];
                }
            else
                return '';
        },
        OnChangeData: function () {
            // This computed property is mixed into the Input data for a schema OnChange event action (see baseFormMixin.jsx)
            return {
                TitleMap: this.lookupitems.map(i => ({
                    value: this.getItemValue(i),
                    record: i,
                }))
            };
        },
        TitleMap: function () {
            return this.lookupitems.map(i => ({
                value: this.getItemValue(i),
                record: i,
            }));
        },
        /** When false, stored value and visible label use the same field so masking during load is unnecessary. */
        lookupDisplayDiffersFromValue() {
            const fd = this.element?.formatData;
            if (!fd) return false;
            return !(fd.DisplayType === 'Fieldname' && fd.ValueType === 'Fieldname'
                && fd.DisplayField === fd.ValueField);
        },
        // Normalize options so QSelect always gets string property names; avoids [object Object] when item_text/item_value are functions. Supports dot notation in field names.
        optionsForSelect() {
            const items = this.lookupitems || [];
            let filtered = items.map((item) => {
                const rawLabel = typeof this.item_text === 'function'
                    ? this.item_text(item)
                    : (typeof this.item_text === 'string' ? this.getByPath(item, this.item_text) : '');
                const label = rawLabel !== null && typeof rawLabel === 'object'
                    ? (rawLabel?.label ?? rawLabel?.text ?? rawLabel?.name ?? JSON.stringify(rawLabel))
                    : String(rawLabel ?? '');
                const value = this.getItemValue(item);
                return { ...item, _label: label, _value: value };
            });

            const v = this.itemvalue;
            const conceal = this.loading && this.lookupDisplayDiffersFromValue
                && !this.element.formatData?.AllowCustomInput
                && v !== undefined && v !== null && v !== '';
            if (conceal && !filtered.some((a) => a._value == v))
                filtered = [{ _label: '\u00a0', _value: v }, ...filtered];

            if (this.filter)
                return filtered.filter(a => a._label.toLowerCase().includes(this.filter.toLowerCase()));

            return filtered;
        },
    },
    methods: {
        /** Get a property from an object by key; supports dot notation (e.g. "Address.City"). */
        getByPath(obj, path) {
            if (obj == null || path == null || typeof path !== 'string') return undefined;
            if (!path.includes('.')) return obj[path];
            const parts = path.split('.');
            let v = obj;
            for (let i = 0; i < parts.length && v != null; i++)
                v = v[parts[i]];
            return v;
        },
        async Refresh() {
            if (this.tolookup) {
                this.loading = true;

                if (this.lookupurlexpn) {
                    let url = this.lookupurl;
                    if (url) {
                        const res = await utils.api.get(url, false, this.element.formatData.CacheLookupResult);
                        // Copy so Not Found / empty-row pushes do not mutate shared API or Var lists.
                        if (res && typeof res === 'object' && Array.isArray(res))
                            this.lookupitems = [...res];
                        else
                            this.lookupitems = [];
                    }
                }
                else if (this.lookuprawexpn) {
                    const raw = this.lookupraw;
                    this.lookupitems = Array.isArray(raw) ? [...raw] : (raw || []);
                }

                const allowsNull = this.element && this.element.schema && this.element.schema.$allows_null ? true : false;
                if (allowsNull)
                    this.appendEmptyLookupRow();

                const allowCustom = this.element.formatData && this.element.formatData.AllowCustomInput;
                const defaultAsEmpty = this.element.formatData && this.element.formatData.DefaultAsEmpty;

                // If no item is already selected, auto-select the first unused item in the list
                if (!allowCustom && !allowsNull && !defaultAsEmpty && Array.isArray(this.lookupitems) && this.lookupitems.length > 0) {
                    if (typeof this.item_value === 'function') {
                        let idx = this.lookupitems.findIndex(a => this.itemvalue == this.item_value(a));
                        if (idx < 0) {
                            if (this.itemvalue !== undefined) {
                                const NotFoundObj = {
                                    $emptyrow: true,
                                    $notfound: true,
                                    [this.element.formatData.DisplayField || 'text']: NOT_FOUND_TEXT,
                                    [this.element.formatData.ValueField || 'value']: this.itemvalue
                                };

                                this.lookupitems.push(NotFoundObj);
                                idx = this.lookupitems.length - 1;
                            }
                            else if (this.arrayfield && this.arrayfield.chosenitems && this.arrayfield.chosenitems[this.chosenitems_key])
                                idx = this.lookupitems.findIndex(a => !(this.item_value(a) in this.arrayfield.chosenitems[this.chosenitems_key]));
                            else
                                idx = 0;

                            if (idx >= 0)
                                this.setValue(this.item_value(this.lookupitems[idx]));
                        }
                    }
                    else if (typeof this.item_value === 'string') {
                        let idx = this.lookupitems.findIndex(a => this.itemvalue == this.getItemValue(a));
                        if (idx < 0) {
                            if (this.itemvalue !== undefined && this.itemvalue !== "") {
                                const NotFoundObj = {
                                    $emptyrow: true,
                                    $notfound: true,
                                    [this.element.formatData.DisplayField || 'text']: NOT_FOUND_TEXT,
                                    [this.element.formatData.ValueField || 'value']: this.itemvalue
                                };

                                this.lookupitems.push(NotFoundObj);
                                idx = this.lookupitems.length - 1;
                            }
                            else if (this.arrayfield && this.arrayfield.chosenitems && this.arrayfield.chosenitems[this.chosenitems_key])
                                idx = this.lookupitems.findIndex(a => !(this.getItemValue(a) in this.arrayfield.chosenitems[this.chosenitems_key]));
                            else
                                idx = 0;

                            if (idx >= 0)
                                this.setValue(this.getItemValue(this.lookupitems[idx]));
                        }
                    }

                    if (this.arrayfield && this.arrayelement)
                        // Update the array container to control if the Add button is enabled or not
                        this.arrayfield.availableitems[this.chosenitems_key] = this.lookupitems && (this.arrayelement.length < this.lookupitems.length);
                }

                this.loading = false;
            }
        },
        emptyItemLabel() {
            return this.element?.formatData?.EmptyItemLabel || '';
        },
        appendEmptyLookupRow() {
            const fd = this.element.formatData;
            const displayField = fd.DisplayField || 'text';
            const valueField = fd.ValueField || 'value';
            const row = { $emptyrow: true };
            if (displayField !== valueField) {
                row[displayField] = this.emptyItemLabel();
                row[valueField] = null;
            }
            this.lookupitems.push(row);
        },
        /** Match Vuetify 2 v-select: getValue falls back to getText when item-value path is undefined. */
        getLookupItemTextValue(item) {
            if (typeof this.item_text === 'function')
                return this.item_text(item);
            if (typeof this.item_text === 'string')
                return this.getByPath(item, this.item_text);
            return undefined;
        },
        getItemValue(item) {
            if (item && item.$emptyrow)
                return null;
            if (typeof this.item_value === 'function')
                return this.item_value(item);
            if (typeof this.item_value === 'string') {
                const value = this.getByPath(item, this.item_value);
                if (value === undefined)
                    return this.getLookupItemTextValue(item);
                return value;
            }
            return null;
        },
        setValue(value) {
            this.sync(value);

            if (this.arrayfield && this.arrayelement) {
                // Uniqueness has been requested and we have a reference to the array of items already picked.
                // We need to generate a lookup object containing every selected item so that we can disable
                // those that are already picked in the current dropdown list.
                this.arrayfield.chosenitems[this.chosenitems_key] = Object.fromEntries(this.arrayelement.map(a => [this.keyextractor(a), true]));

                // Update the array container to control if the Add button is enabled or not
                this.arrayfield.availableitems[this.chosenitems_key] = this.lookupitems && (this.arrayelement.length < this.lookupitems.length);
            }
        },
        filterFn(val, update) {
            update(() => this.filter = val);
        },
    },
    render() {
        const variableField = this.tryRenderVariableBindingField({
            class: 'caption sform-input-white c-variable-binding-select',
            labelSlot: true,
        });
        if (variableField)
            return variableField;

        const useInput = !!(this.element.formatData?.AllowCustomInput || this.element.formatData?.AutoCompleteInput);
        const keyup = this.element.formatData?.AllowCustomInput ? (e) => this.setValue(e.target.value) : () => {};

        let slots = {};
        if (this.hasInputLabel)
            slots.label = () => <TranslationContainer context={this} value={this.labelText} autoOpenModal></TranslationContainer>;
        slots['no-option'] = () => (
            <QItem disable>
                <QItemSection>
                    <QItemLabel>
                        <TranslationContainer context={this} value="No data available" />
                    </QItemLabel>
                </QItemSection>
            </QItem>
        );
        if (this.hintText)
            slots.hint = () => <TranslationContainer context={this} value={this.hintText} autoOpenModal></TranslationContainer>;

        slots = this.mergeVariableAssistSlots(slots);
        const fieldDisabled = !!(this.readonly || this.appearance?.disabled || this.appearance?.readonly || this.noinput);

        let input = (
            <QSelect
                ref="form"
                rules={this.rules} {...this.compilerErrorFieldProps}
                class="caption sform-input-white"
                outlined
                dense
                labelSlot={this.hasInputLabel}
                xhint={this.inputHint}
                hide-hint
                popup-content-class="q-select-max-popup-height"
                modelValue={this.itemvalue}
                options={this.optionsForSelect}
                options-dense
                loading={this.loading}
                option-label="_label"
                option-value="_value"
                option-disable={this.item_disabled || 'disabled'}
                disable={fieldDisabled}
                readonly={fieldDisabled}
                input-debounce={0}
                emit-value
                map-options
                style={{width: "100%"}}
                use-input={useInput}
                hideSelected={useInput}
                fillInput={useInput}
                onFilter={this.filterFn}
                newValueMode="add-unique"
                onUpdate:modelValue={(value) => this.setValue(value)}
                onKeyup={keyup}
                onFocus={() => { this._vbFocused = true; this.onFocus(); }}
                onBlur={() => { this._vbFocused = false; this.onBlur(); }}
                bottom-slots={this.showFieldBottomSlots}
            >
                {slots}
            </QSelect>
        );

        if (this.appearance)
            utils.applyVue2ToVue3Props(input, this.appearance);
        //input.componentOptions.propsData = { ...input.componentOptions.propsData, ...this.appearance };

        // if (this.directives && this.directives.some(a => a.name == 'on-enter-default'))
        //    input.props.autofocus = true;

        if (this.directives) {
            var applyDirectives = this.directives.map(d => {
                const dirDef = d.directive || resolveDirective(d.name);

                return [dirDef, d.value, d.arg, d.modifiers];
            });
            input = withDirectives(input, applyDirectives);
        }

        return this.wrapLiteralWithVariableAssist(input);
    }
});